import { assign, createMachine, MachineConfig, MachineOptions } from 'xstate'

import { GUEST_ID, TAuthMachineTypes } from './authMachineTypes'

type TContext = TAuthMachineTypes['context']
type TEvent = TAuthMachineTypes['events']
type TTypeState = TAuthMachineTypes['typeState']
type TStateSchema = TAuthMachineTypes['schema']

const machineConfig: MachineConfig<TContext, TStateSchema, TEvent> = {
  predictableActionArguments: true,
  id: 'auth',
  initial: 'init',
  context: {
    userId: null,
    userToken: null,
    guestToken: null,
    error: null,
  },
  on: {
    SET_AUTH_DATA: { target: '#user', actions: ['assignAuth', 'onContextChange'] },
    CLEAR_ERROR: { actions: ['assignAuth', 'onContextChange'] },
  },
  states: {
    init: {
      invoke: {
        id: 'initContext',
        src: 'initContext',
        onDone: {
          actions: ['assignAuth', 'onContextChange'],
          target: '#validate',
        },
        onError: '#validate',
      },
    },
    validate: {
      id: 'validate',
      always: [
        { target: '#logGuest', cond: 'isEmpty' }, //
        { target: '#guest', cond: 'isGuest' }, //
        { target: '#user' },
      ],
    },
    logGuest: {
      id: 'logGuest',
      invoke: {
        id: 'obtainGuestToken',
        src: 'obtainGuestToken',
        onDone: {
          target: '#guest',
          actions: ['assignAuth', 'onContextChange'],
        },
        onError: {
          target: '#failed',
          actions: ['assignAuth', 'onContextChange'],
        },
      },
    },
    logUser: {
      id: 'logUser',
      invoke: {
        id: 'obtainUserToken',
        src: 'obtainUserToken',
        onDone: {
          target: '#user',
          actions: ['assignAuth', 'onContextChange'],
        },
        onError: {
          target: '#guest',
          actions: ['assignAuth', 'onContextChange'],
        },
      },
    },
    guest: {
      id: 'guest',
      on: {
        LOGIN: { target: '#logUser' },
        LOGOUT: { target: '#logGuest' },
      },
    },
    user: {
      id: 'user',
      on: { LOGOUT: { target: '#logGuest' } },
    },
    failed: {
      id: 'failed',
      on: {
        LOGIN: { target: '#logUser' },
        LOGOUT: { target: '#logGuest' },
      },
    },
  },
}

const machineOptions: Partial<MachineOptions<TContext, TEvent>> = {
  actions: {
    assignAuth: assign((context, event) => {
      // attempt to obtain a new guest token has failed
      // -> clear the context
      if (event.type === 'error.platform.obtainGuestToken') {
        return {
          ...context,
          userToken: null,
          userId: null,
          error: event.data,
        }
      }

      // attempt to obtain a new user token has failed
      // -> keep the context (guest values), store error data
      if (event.type === 'error.platform.obtainUserToken') {
        return {
          ...context,
          error: event.data,
        }
      }

      // the context was initialized (from persistence)
      // -> set it as a new context along with the user ID
      if (event.type === 'done.invoke.initContext') {
        return {
          ...context,
          guestToken: event.data?.guestToken ?? null,
          userToken: event.data?.userToken ?? null,
          userId: event.data?.userId ?? null,
          error: null,
        }
      }

      // a new guest token was successfully obtained
      // -> set it as a new context along with the guest user ID
      // -> clear the previous user token
      // -> keep the error value (to display it in the UI)
      if (event.type === 'done.invoke.obtainGuestToken') {
        return {
          ...context,
          userToken: null,
          guestToken: event.data.token,
          userId: String(event.data.userId),
        }
      }

      // a new user token was successfully obtained
      // -> set it as a new context along with the user ID
      // -> keep the guest token
      // -> clear the error value
      if (event.type === 'done.invoke.obtainUserToken') {
        return {
          ...context,
          error: null,
          userToken: event.data.token,
          userId: String(event.data.userId),
        }
      }

      // directly set auth data
      if (event.type === 'SET_AUTH_DATA') {
        return {
          ...context,
          ...event.payload,
          error: null,
        }
      }

      // clear error value
      if (event.type === 'CLEAR_ERROR') {
        return {
          ...context,
          error: null,
        }
      }

      return context
    }),
  },
  guards: {
    isEmpty: (context) => !Boolean(context.userId && (context.userToken || context.guestToken)),
    isGuest: (context) => context.userId === GUEST_ID,
  },
}

export const authMachine = createMachine<TContext, TEvent, TTypeState>(
  machineConfig,
  machineOptions,
)
